import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { notificacionService, API_BASE_URL } from '../../services/api';
import axios from 'axios';
import {
  AdminContainer, AdminTopBar, AdminInfoText,
  AdminButton, AdminActionBtn, AdminToggle,
  AdminTable, AdminTh, AdminTd,
  AdminBadge,
  AdminModalOverlay, AdminModalContent, AdminModalTitle, AdminModalActions,
  AdminFormGroup, AdminLabel, AdminInput, AdminTextarea, AdminSelect,
  AdminLoading
} from './AdminShared';

const tipoBadgeColors = {
  warning: { bg: '#fef3c7', color: '#92400e' },
  success: { bg: '#d1fae5', color: '#065f46' },
  info: { bg: '#dbeafe', color: '#1e40af' },
};

const AdminNotificaciones = () => {
  const [notificaciones, setNotificaciones] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [saving, setSaving] = useState(false);

  // Form state
  const [formTitulo, setFormTitulo] = useState('');
  const [formMensaje, setFormMensaje] = useState('');
  const [formTipo, setFormTipo] = useState('info');
  const [formRolDestino, setFormRolDestino] = useState('todos');

  const fetchNotificaciones = useCallback(async () => {
    try {
      const res = await notificacionService.getAll();
      if (res.data?.success) {
        setNotificaciones(res.data.notificaciones);
      }
    } catch (err) {
      console.error('Error cargando notificaciones:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchRoles = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/roles.php`);
      if (res.data?.roles) {
        setRoles(res.data.roles);
      }
    } catch (err) {
      console.error('Error cargando roles:', err);
    }
  }, []);

  useEffect(() => {
    fetchNotificaciones();
    fetchRoles();
  }, [fetchNotificaciones, fetchRoles]);

  const openCreateModal = () => {
    setEditItem(null);
    setFormTitulo('');
    setFormMensaje('');
    setFormTipo('info');
    setFormRolDestino('todos');
    setShowModal(true);
  };

  const openEditModal = (item) => {
    setEditItem(item);
    setFormTitulo(item.titulo);
    setFormMensaje(item.mensaje);
    setFormTipo(item.tipo);
    setFormRolDestino(item.rol_destino || 'todos');
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!formTitulo.trim() || !formMensaje.trim()) {
      toast.error('Titulo y mensaje son requeridos');
      return;
    }

    setSaving(true);
    try {
      const data = {
        titulo: formTitulo,
        mensaje: formMensaje,
        tipo: formTipo,
        rol_destino: formRolDestino === 'todos' ? null : formRolDestino
      };

      if (editItem) {
        await notificacionService.update({ ...data, id: editItem.id });
        toast.success('Notificacion actualizada');
      } else {
        await notificacionService.create(data);
        toast.success('Notificacion creada exitosamente');
      }
      setShowModal(false);
      fetchNotificaciones();
    } catch (err) {
      const msg = err.response?.data?.error || 'Error guardando notificacion';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (item) => {
    try {
      await notificacionService.update({ id: item.id, activa: item.activa === '1' ? 0 : 1 });
      toast.success(item.activa === '1' ? 'Notificacion desactivada' : 'Notificacion activada');
      fetchNotificaciones();
    } catch (err) {
      toast.error('Error cambiando estado');
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Eliminar la notificacion "${item.titulo}"?`)) return;

    try {
      await notificacionService.delete(item.id);
      toast.success('Notificacion eliminada');
      fetchNotificaciones();
    } catch (err) {
      const msg = err.response?.data?.error || 'Error eliminando notificacion';
      toast.error(msg);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    return d.toLocaleDateString('es-CO', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  if (loading) return <AdminLoading>Cargando notificaciones...</AdminLoading>;

  return (
    <AdminContainer>
      <AdminTopBar>
        <AdminInfoText>
          Las notificaciones se muestran como modal a los usuarios al cargar la app. Puedes dirigirlas a un rol o a todos.
        </AdminInfoText>
        <AdminButton onClick={openCreateModal}>+ Nueva Notificacion</AdminButton>
      </AdminTopBar>

      <AdminTable>
        <thead>
          <tr>
            <AdminTh>Titulo</AdminTh>
            <AdminTh>Tipo</AdminTh>
            <AdminTh>Destino</AdminTh>
            <AdminTh>Estado</AdminTh>
            <AdminTh>Leidas</AdminTh>
            <AdminTh>Fecha</AdminTh>
            <AdminTh>Acciones</AdminTh>
          </tr>
        </thead>
        <tbody>
          {notificaciones.map(n => {
            const badgeColor = tipoBadgeColors[n.tipo] || tipoBadgeColors.info;
            return (
              <tr key={n.id}>
                <AdminTd style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {n.titulo}
                </AdminTd>
                <AdminTd>
                  <AdminBadge $bg={badgeColor.bg} $color={badgeColor.color}>{n.tipo}</AdminBadge>
                </AdminTd>
                <AdminTd>{n.rol_destino || 'Todos'}</AdminTd>
                <AdminTd>
                  <AdminToggle $active={n.activa === '1'} onClick={() => handleToggle(n)}>
                    {n.activa === '1' ? 'Activa' : 'Inactiva'}
                  </AdminToggle>
                </AdminTd>
                <AdminTd>{n.leidas_count}</AdminTd>
                <AdminTd style={{ fontSize: '0.8rem', color: '#6b7280' }}>{formatDate(n.created_at)}</AdminTd>
                <AdminTd>
                  <AdminActionBtn onClick={() => openEditModal(n)}>Editar</AdminActionBtn>
                  <AdminActionBtn $danger onClick={() => handleDelete(n)}>Eliminar</AdminActionBtn>
                </AdminTd>
              </tr>
            );
          })}
          {notificaciones.length === 0 && (
            <tr>
              <AdminTd colSpan="7" style={{ textAlign: 'center', color: '#9ca3af', padding: '2rem' }}>
                No hay notificaciones creadas
              </AdminTd>
            </tr>
          )}
        </tbody>
      </AdminTable>

      {showModal && (
        <AdminModalOverlay onClick={() => setShowModal(false)}>
          <AdminModalContent onClick={e => e.stopPropagation()}>
            <AdminModalTitle>{editItem ? 'Editar Notificacion' : 'Nueva Notificacion'}</AdminModalTitle>

            <AdminFormGroup>
              <AdminLabel>Titulo</AdminLabel>
              <AdminInput
                value={formTitulo}
                onChange={e => setFormTitulo(e.target.value)}
                placeholder="Ej: Mantenimiento programado"
              />
            </AdminFormGroup>

            <AdminFormGroup>
              <AdminLabel>Mensaje</AdminLabel>
              <AdminTextarea
                value={formMensaje}
                onChange={e => setFormMensaje(e.target.value)}
                placeholder="Contenido del mensaje para los usuarios..."
              />
            </AdminFormGroup>

            <AdminFormGroup>
              <AdminLabel>Tipo</AdminLabel>
              <AdminSelect value={formTipo} onChange={e => setFormTipo(e.target.value)}>
                <option value="info">Informacion</option>
                <option value="warning">Advertencia</option>
                <option value="success">Exito</option>
              </AdminSelect>
            </AdminFormGroup>

            <AdminFormGroup>
              <AdminLabel>Rol destino</AdminLabel>
              <AdminSelect value={formRolDestino} onChange={e => setFormRolDestino(e.target.value)}>
                <option value="todos">Todos los usuarios</option>
                {roles.map(r => (
                  <option key={r.name} value={r.name}>{r.label}</option>
                ))}
              </AdminSelect>
            </AdminFormGroup>

            <AdminModalActions>
              <AdminButton $ghost onClick={() => setShowModal(false)}>Cancelar</AdminButton>
              <AdminButton onClick={handleSave} disabled={saving}>
                {saving ? 'Guardando...' : (editItem ? 'Actualizar' : 'Crear Notificacion')}
              </AdminButton>
            </AdminModalActions>
          </AdminModalContent>
        </AdminModalOverlay>
      )}
    </AdminContainer>
  );
};

export default AdminNotificaciones;
